import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND, waOrder } from "../../data/zingly";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "menu", label: "Menu" },
  { id: "about", label: "About" },
  { id: "reviews", label: "Reviews" },
  { id: "contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (id === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav shadow-[0_4px_24px_rgba(0,0,0,0.05)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
        <button
          onClick={() => go("home")}
          className="flex items-center gap-3 group"
          data-testid="brand-logo"
        >
          <span className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden bg-white shadow-lg ring-2 ring-[#F58220]/30 group-hover:ring-[#F58220] transition-all group-hover:scale-105">
            <img src={BRAND.logo} alt="Zingly Dosa" className="w-full h-full object-cover" />
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-xl md:text-2xl font-extrabold text-[#1E1E1E] tracking-tight group-hover:text-[#F58220] transition-colors">
              Zingly <span className="text-[#2E7D32]">Dosa</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#F58220] font-semibold">
              Home Made • Fresh Daily
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="nav-link text-[15px] font-medium text-[#1E1E1E]"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={waOrder()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-order-now-btn"
            className="btn-3d inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#F58220] to-[#E16608] text-white text-sm font-semibold"
          >
            Order Now
          </a>
        </div>

        <button
          className="lg:hidden p-2 rounded-full bg-white/80 backdrop-blur border border-orange-100"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5 text-[#1E1E1E]" /> : <Menu className="w-5 h-5 text-[#1E1E1E]" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden glass-nav border-t border-orange-100"
            data-testid="mobile-menu-panel"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left py-3 px-3 rounded-xl text-[#1E1E1E] font-medium hover:bg-[#FFF8F1] hover:text-[#F58220] transition"
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <a
                href={waOrder()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-order-now-btn"
                className="mt-2 text-center px-6 py-3 rounded-full bg-gradient-to-r from-[#F58220] to-[#E16608] text-white font-semibold"
              >
                Order Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
